// The public site's LINK MODEL — one derivation, two chromes, six themes.
//
// Two bugs share this module. BUG-026: four of the six themes had no functional footer, and
// Classic's hardcoded the SEED tenant's identity (a person's name and her Instagram/Facebook
// profiles) onto every practitioner's site. BUG-006: each theme's header offered four or five
// SAME-PAGE anchors and a Book button, and nothing else — no /programmes, /boutique,
// /journal, /learn or /compte link existed in any theme, so the interior pages of a themed
// site were unreachable from its own navigation.
//
// Both had the same cause: every chrome invented its own list of destinations. So the site's
// sitemap is derived HERE, once, from the same three inputs the rest of the public site
// already agrees on (`useSiteCopy()`, the tenant record, `section_toggles`), and each chrome only
// decides how to draw it. No JSX and no React in this module: it is a pure function over
// data, testable on its own.
//
// Shape returned by `buildNavModel` (units: all strings are already localized):
//   {
//     brand:    string
//     home:     string                                   // tenant-aware "/"
//     sections: Array<{ key, href, label }>               // 0..5 homepage anchors, "/#key"
//     pages:    Array<{ key, href, label }>               // 3..6 interior routes
//     account:  { key, href, label } | null               // /compte
//     book:     { key, href, label } | null               // /reserver, the CTA
//   }
//
// Shape returned by `buildFooterModel`:
//   {
//     brand:    string                                    // tenant.name -> owner_name -> fallback
//     tagline:  string
//     rights:   string
//     built:    string
//     year:     number
//     headings: { navigate, explore, contact, social }     // 4 localized column titles
//     sections: Array<{ key, href, label }>                // same 0..5 anchors as the nav
//     pages:    Array<{ key, href, label }>                // same 3..6 interior routes
//     contact:  { email: string, place: string }
//     socials:  Array<{ key, url, label }>                 // 0..6, only non-empty fields
//   }

// Relative import on purpose (sibling module in lib/, zero dependencies of its own): the
// "@/" alias is not mapped on the Jest side, so a relative path keeps this module — and
// the real href derivation with it — under test without a virtual mock.
import { tenantHref, tenantHomePath } from "./tenantPath";

/**
 * The homepage sections offered as anchors, in reading order. Both chromes use this list.
 *
 * `blog` is deliberately ABSENT even though it is a toggleable section: the journal has a
 * real interior page (`/journal`, the full archive) and it is listed in `INTERIOR_PAGES`
 * below. Listing both would put the same label twice in the same menu, and of the two
 * destinations the archive is the one worth linking. This is also why Classic's header lost
 * its `/#blog` entry: it now links `/journal`, like every theme and both footers.
 */
export const ANCHOR_SECTIONS = [
  { key: "story", hash: "/#story", navKey: "story" },
  { key: "approach", hash: "/#approach", navKey: "approach" },
  { key: "services", hash: "/#services", navKey: "services" },
  { key: "testimonials", hash: "/#testimonials", navKey: "testimonials" },
  { key: "contact", hash: "/#contact", navKey: "contact" },
];

/**
 * The interior pages the site links to, in reading order.
 *
 * The course catalogue is gated on `has_published_courses === true`, strictly: the flag is
 * computed per request by GET /api/saas/tenant/{slug} (saas_routes._has_published_courses),
 * and an unknown-path HTML payload or a missing key must degrade to "no link" rather than to
 * a link into an empty catalogue.
 *
 * The pathways page (`/parcours`) is gated the same way and for the same reason, on
 * `has_active_pathways === true`: GET /api/saas/tenant/{slug} computes it per request with
 * the predicate GET /api/public/pathways lists by (`status == "published"`), so the link is
 * offered exactly when the page it opens has a pathway to show
 * (docs/pathways-module/pathways-api-contract.md, §6 "Nav flag").
 *
 * There is no `/consultations` entry (BUG-005's paid video consultation page, and its
 * `has_telehealth_services` gate, were both retired with the legacy prepaid telehealth
 * lane): per-minute telehealth is now discovered through the per-minute service card on
 * `/reserver`, like any other bookable service, not through a dedicated nav link.
 *
 * `journal` additionally follows the `blog` section toggle: a practice that switched its
 * journal off does not advertise an empty archive.
 */
export const INTERIOR_PAGES = [
  { key: "reserver", path: "/reserver", navKey: "reserve" },
  { key: "boutique", path: "/boutique", navKey: "shop" },
  { key: "programmes", path: "/programmes", navKey: "programs" },
  { key: "parcours", path: "/parcours", navKey: "pathways", requiresPathways: true },
  { key: "journal", path: "/journal", navKey: "blog", section: "blog" },
  { key: "learn", path: "/learn", navKey: "courses", requiresCourses: true },
];

/**
 * The social networks a tenant can publish, in reading order. `field` is the key under
 * `tenant.branding` (backend: `tenant_self_service.SOCIAL_BRANDING_FIELDS`); `label` is a
 * proper noun, which is why it is not routed through the locale layer.
 */
export const SOCIAL_PLATFORMS = [
  { key: "instagram", field: "instagram_url", label: "Instagram" },
  { key: "facebook", field: "facebook_url", label: "Facebook" },
  { key: "linkedin", field: "linkedin_url", label: "LinkedIn" },
  { key: "youtube", field: "youtube_url", label: "YouTube" },
  { key: "tiktok", field: "tiktok_url", label: "TikTok" },
  { key: "x", field: "x_url", label: "X" },
];

/**
 * Only absolute http(s) URLs are rendered. The backend validator rejects anything else on
 * write, but the footer is the thing that puts the value in an `href`, so it refuses a
 * `javascript:` payload on its own rather than trusting a document written by an older
 * build (or restored from a backup) to have been validated.
 */
const isSafeHttpUrl = (raw) => {
  if (typeof raw !== "string") return false;
  const value = raw.trim();
  if (!value) return false;
  return /^https?:\/\/[^\s]+$/i.test(value);
};

/**
 * The public brand name of a practice: its business name, then the holder's name, then a
 * localized placeholder. Same chain the transactional e-mails use
 * (backend/emails/shared.py `_tenant_brand`), so the site and the mail a visitor receives
 * after writing through the contact form name the practice identically.
 *
 * @param {object|null} tenant    tenant record (TenantContext)
 * @param {string}      fallback  localized placeholder, e.g. useT("templates")("shared.brand.fallback")
 * @returns {string}
 */
export const resolveBrandName = (tenant, fallback = "") =>
  tenant?.name || tenant?.owner_name || fallback;

/** `tenant.section_toggles` semantics: a key is ON unless it is exactly `false`. */
const showSection = (toggles, key) => toggles?.[key] !== false;

/**
 * A link whose label did not resolve is dropped, not rendered blank: an unlabelled anchor is
 * invisible to a reader and still clickable, which is worse than absent. It is also what
 * keeps a bare dictionary (or a locale the copy has not reached yet) from producing a menu
 * of empty rows.
 */
const labelled = (link) => Boolean(link.label);

/**
 * Anchors follow HOME, not the bare path: "/#story" is a fragment navigation when the
 * visitor is already on the homepage (no reload) and a real navigation home when the
 * same menu is rendered under an interior page, where a bare "#story" would scroll to
 * nothing. `home` already carries the tenant context (`tenantHomePath`): "/" on a
 * tenant host, "/t/{slug}" in path mode, "/demo" for the root tenant on the platform
 * host — so "/t/{slug}/#story" and "/demo/#story" fall out of one formula instead of
 * `tenantHref`, which knows the path prefix but not the tenant document (its "/#story"
 * on /demo pointed the demo site's own menu at the SaaS landing page).
 */
const deriveSections = (nav, toggles, home) =>
  ANCHOR_SECTIONS
    .filter((s) => showSection(toggles, s.key))
    .map((s) => ({
      key: s.key,
      href: home === "/" ? s.hash : `${home}${s.hash}`,
      label: nav[s.navKey] || "",
    }))
    .filter(labelled);

const derivePages = (nav, tenant, toggles) =>
  INTERIOR_PAGES
    .filter((p) => (p.section ? showSection(toggles, p.section) : true))
    .filter((p) => (p.requiresCourses ? tenant?.has_published_courses === true : true))
    .filter((p) => (p.requiresPathways ? tenant?.has_active_pathways === true : true))
    .map((p) => ({ key: p.key, href: tenantHref(p.path), label: nav[p.navKey] || "" }))
    .filter(labelled);

/**
 * The navigation model — the header's destinations, for Classic and for the five themes.
 *
 * @param {object}   args.siteCopy  resolved public copy (`useSiteCopy()`, i.e. dictionary +
 *                                  the tenant's content_blocks overrides + the tenant record)
 * @param {object}   args.tenant    tenant record from TenantContext (may be null: the
 *                                  public site renders before it resolves)
 * @param {object}   args.toggles   `tenant.section_toggles`
 * @param {string}   [args.brand]   pre-resolved brand name (the chromes already compute one
 *                                  with a localized fallback); derived here otherwise
 * @returns {object} the nav model documented at the top of this file
 */
export function buildNavModel({ siteCopy, tenant, toggles = {}, brand } = {}) {
  const copy = siteCopy || {};
  const nav = copy.nav || {};
  const home = tenantHomePath(tenant);

  const account = { key: "account", href: tenantHref("/compte"), label: nav.account || "" };
  const book = { key: "book", href: tenantHref("/reserver"), label: nav.book || "" };

  return {
    brand: brand || resolveBrandName(tenant, copy.footer?.brand),
    home,
    sections: deriveSections(nav, toggles, home),
    pages: derivePages(nav, tenant, toggles),
    // The client area and the Book CTA are not part of the sitemap columns: they are the
    // two ACTIONS every chrome puts next to its menu, and a chrome renders them apart from
    // the link rows. `null` when the copy has no label rather than an empty button.
    account: labelled(account) ? account : null,
    book: labelled(book) ? book : null,
  };
}

/**
 * The footer model — the same sitemap plus the practice's identity, contact and socials.
 *
 * @param {object}   args.siteCopy  resolved public copy (`useSiteCopy()`); its
 *                                  `contact.info.email` is relayed as-is, see below
 * @param {object}   args.tenant    tenant record (may be null)
 * @param {object}   args.toggles   `tenant.section_toggles`
 * @param {string}   [args.brand]   pre-resolved brand name
 * @returns {object} the footer model documented at the top of this file
 */
export function buildFooterModel({ siteCopy, tenant, toggles = {}, brand } = {}) {
  const copy = siteCopy || {};
  const nav = copy.nav || {};
  const footer = copy.footer || {};
  const branding = tenant?.branding || {};
  const home = tenantHomePath(tenant);

  const socials = SOCIAL_PLATFORMS
    .filter((p) => isSafeHttpUrl(branding[p.field]))
    .map((p) => ({ key: p.key, url: branding[p.field].trim(), label: p.label }));

  return {
    brand: brand || resolveBrandName(tenant, footer.brand),
    tagline: footer.tagline || "",
    rights: footer.rights || "",
    built: footer.built || "",
    year: new Date().getFullYear(),
    headings: {
      navigate: footer.navigate || "",
      explore: footer.explore || "",
      contact: nav.contact || "",
      social: footer.social || "",
    },
    sections: deriveSections(nav, toggles, home),
    pages: derivePages(nav, tenant, toggles),
    // Straight from the resolved copy: `siteCopy.contact.info.email` already IS
    // "saved override, else the practice's business/owner e-mail, else the bundled sample"
    // (lib/useSiteCopy.js -> resolveSiteCopy). This used to re-derive that chain here, by
    // testing the resolved value against the literal sample address and swapping the tenant's
    // in — a sentinel that made the footer right on a page whose contact SECTION was still
    // printing "contact@example.com", i.e. it hid the real defect instead of fixing it.
    contact: {
      email: copy.contact?.info?.email || "",
      place: copy.contact?.info?.place || "",
    },
    socials,
  };
}
